import { FormEvent, lazy, Suspense, useEffect, useState } from "react";
import { QuiddityMark } from "@quiddity/brand";
import { QuiddityActivity } from "@quiddity/activity";
import { FunctionalSymbol } from "@quiddity/icons";
import "@quiddity/icons/styles.css";
import { currentJectHandoff, JectQuaeroHandoff } from "./JectQuaeroHandoff";

const EnginePortfolio = lazy(() =>
  import("./EnginePortfolio").then((module) => ({ default: module.EnginePortfolio })),
);
const PromptLibrary = lazy(() =>
  import("./PromptLibrary").then((module) => ({ default: module.PromptLibrary })),
);
const HollowHallway = lazy(() =>
  import("./HollowHallway").then((module) => ({ default: module.HollowHallway })),
);
const SemanticReview = lazy(() =>
  import("./SemanticReview").then((module) => ({ default: module.SemanticReview })),
);

type SessionProfile = {
  email?: string;
  preferred_username?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  identity_provider?: string;
};

type ProtectedSession =
  | { status: "idle" | "checking" | "unauthenticated" | "error"; profile: null }
  | { status: "authenticated"; profile: SessionProfile };

type QuaeroCandidate = {
  candidateEventId: string;
  sourceText: string;
  statement: string;
  construct: "idea" | "belief" | "agreement";
  rationale: string;
  confidence: string;
  signals: string[];
  provenance: {
    method: string;
    producer: string;
    model?: string | null;
    fallback: boolean;
    fallbackClass?: string | null;
  };
};

type QuaeroQuestion = {
  questionEventId: string;
  requestId: string;
  fieldKey: string;
  prompt: string;
  why: string;
  required: boolean;
  responseType: "text" | "single-select";
  options: string[];
};

type QuaeroRouting = {
  status: "proposed";
  capability: string;
  destinationType: "authorized-person-or-team";
  rationale: string;
  assignmentStatus: "unassigned";
  requiresAuthorizedRouter: true;
};

type QuaeroHandoff = {
  handoffEventId: string;
  requestId: string;
  status: "handoff-ready";
  proposedTitle: string;
  workType: string;
  subject: string;
  requestedOutcome: string;
  acceptanceSignals: string;
  details: Record<string, string>;
  sourceExcerpts: string[];
  openUncertainties: string[];
  routing: QuaeroRouting;
};

type QuaeroRequestFormation = {
  requestId: string;
  sourceSegmentIds: string[];
  sourceTexts: string[];
  proposedTitle: string;
  workType: string;
  subject: string;
  classificationConfidence: "low" | "medium" | "high";
  classificationRationale: string;
  state: "gathering-details" | "sufficient" | "complete";
  fields: Record<string, string>;
  missingRequiredFields: string[];
  unresolvedCandidateCount: number;
  supportingReadingCount: number;
  routing: QuaeroRouting;
  nextQuestion?: QuaeroQuestion | null;
  canPrepareHandoff: boolean;
  handoff?: QuaeroHandoff | null;
};

type QuaeroResponse = {
  streamId: string;
  candidates: QuaeroCandidate[];
  requests: QuaeroRequestFormation[];
};

type QuaeroStreamResponse = {
  streamId: string;
  requests: QuaeroRequestFormation[];
};

const FICTIONAL_EXAMPLE =
  "I want an authorized teammate to test a fictional eight-hour weekly commitment for four weeks. Success means the result is reviewed and a written recommendation is returned after four weeks.";

const humanize = (value: string) =>
  value
    .split("-")
    .map((part) =>
      part.toUpperCase() === part
        ? part
        : `${part.charAt(0).toUpperCase()}${part.slice(1)}`,
    )
    .join(" ");

function useMotionPreference() {
  const [staticMode, setStaticMode] = useState(() => {
    const saved = window.sessionStorage.getItem("quiddity-motion");
    if (saved) return saved === "static";
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
    );
  });

  const toggle = () => {
    setStaticMode((current) => {
      const next = !current;
      window.sessionStorage.setItem(
        "quiddity-motion",
        next ? "static" : "living",
      );
      return next;
    });
  };

  return { staticMode, toggle };
}

function OrbitalMotionToggle({
  staticMode,
  onToggle,
  size,
}: {
  staticMode: boolean;
  onToggle: () => void;
  size: number;
}) {
  const label = staticMode ? "Resume orbital motion" : "Pause orbital motion";
  return (
    <button
      className="orbital-motion-toggle"
      type="button"
      onClick={onToggle}
      aria-label={label}
      aria-pressed={staticMode}
      title={label}
    >
      <QuiddityMark
        decorative
        size={size}
        motion={staticMode ? "static" : "auto"}
      />
      <span className="sr-only">
        {staticMode ? "Orbital motion is paused." : "Orbital motion is playing."}
      </span>
    </button>
  );
}

const ACCOUNT_CONNECTIONS_URL =
  "https://auth.quiddity.live/realms/quiddity/account/#/account-security/linked-accounts";

const cleanProfileValue = (value?: string) => value?.trim() ?? "";

function profileFullName(profile: SessionProfile) {
  const structuredName = [profile.given_name, profile.family_name]
    .map(cleanProfileValue)
    .filter(Boolean)
    .join(" ");
  return structuredName || cleanProfileValue(profile.name);
}

function profileFirstName(profile: SessionProfile) {
  const givenName = cleanProfileValue(profile.given_name);
  if (givenName) return givenName;
  const fullName = profileFullName(profile);
  return fullName && !fullName.includes("@") ? fullName.split(/\s+/)[0] : "";
}

function profileInitials(profile: SessionProfile) {
  const givenName = cleanProfileValue(profile.given_name);
  const familyName = cleanProfileValue(profile.family_name);
  if (givenName && familyName) {
    return `${givenName[0]}${familyName[0]}`.toUpperCase();
  }
  const fullName = profileFullName(profile);
  if (!fullName || fullName.includes("@")) return "";
  const parts = fullName.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return "";
  return `${parts[0]?.[0] ?? ""}${parts.length > 1 ? parts.at(-1)?.[0] ?? "" : ""}`.toUpperCase();
}

function AccountMenu({ profile }: { profile: SessionProfile }) {
  const [open, setOpen] = useState(false);
  const preferredAccountName = cleanProfileValue(profile.preferred_username);
  const participantName =
    profileFullName(profile) ||
    (preferredAccountName && !preferredAccountName.includes("@")
      ? preferredAccountName
      : "Participant");
  const initials = profileInitials(profile);
  const activeProvider = profile.identity_provider?.toLowerCase();
  const providerState = (provider: "google" | "linkedin") =>
    activeProvider === provider
      ? "Connected for this session"
      : "View connection state";

  return (
    <div className="account-menu">
      <button
        className="account-menu__trigger"
        type="button"
        aria-label={`Account for ${participantName}`}
        aria-expanded={open}
        aria-controls="account-menu-panel"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="account-menu__avatar" aria-hidden="true">
          {initials || (
            <FunctionalSymbol
              semantic="account.profile"
              showLabel={false}
              decorative
            />
          )}
        </span>
        <span>Account</span>
      </button>
      {open ? (
        <section
          className="account-menu__panel"
          id="account-menu-panel"
          aria-label="Quiddity account management"
        >
          <p className="account-menu__identity">{participantName}</p>
          <p className="account-menu__meta">Authorized Quiddity account</p>
          <ul className="account-menu__providers" aria-label="Identity providers">
            <li>
              <span>Google</span>
              <span>{providerState("google")}</span>
            </li>
            <li>
              <span>LinkedIn</span>
              <span>{providerState("linkedin")}</span>
            </li>
          </ul>
          <a className="account-menu__manage" href={ACCOUNT_CONNECTIONS_URL}>
            Manage sign-in connections
          </a>
          <p className="account-menu__note">
            Keycloak shows the authoritative linked state and prevents unsafe
            connection changes.
          </p>
        </section>
      ) : null}
    </div>
  );
}

function Header({
  protectedView,
  profile,
}: {
  protectedView: boolean;
  profile?: SessionProfile | null;
}) {
  return (
    <header className="site-header">
      <a className="wordmark" href="/" aria-label="Quiddity home">
        <span className="wordmark__spark" aria-hidden="true" />
        Quiddity
      </a>
      <nav className="site-header__actions" aria-label="Primary navigation">
        {protectedView ? (
          <>
            {profile ? <AccountMenu profile={profile} /> : null}
            <a className="quiet-action" href="/oauth2/sign_out?rd=/">
              <FunctionalSymbol
                semantic="common.logout"
                showLabel={false}
                decorative
              />
              Exit
            </a>
          </>
        ) : (
          <a className="threshold-action" href="/app">
            <FunctionalSymbol
              semantic="common.next"
              showLabel={false}
              decorative
            />
            Enter Quiddity
          </a>
        )}
      </nav>
    </header>
  );
}

function Landing({
  staticMode,
  onToggleMotion,
}: {
  staticMode: boolean;
  onToggleMotion: () => void;
}) {
  return (
    <main id="main-content">
      <section className="landing-hero" aria-labelledby="landing-title">
        <div className="landing-hero__copy">
          <p className="eyebrow">Harmony without sameness</p>
          <h1 id="landing-title">
            A living foundation for distinct intelligences.
          </h1>
          <p className="lede">
            Quiddity brings focused Engines into one coherent world—each
            preserving its own responsibility while helping people, ideas, and
            experiences discover their true whatness.
          </p>
          <div className="hero-actions">
            <a className="primary-action" href="/app">
              <FunctionalSymbol
                semantic="common.next"
                showLabel={false}
                decorative
              />
              Enter the command center
            </a>
          </div>
        </div>
        <div className="landing-hero__mark">
          <OrbitalMotionToggle
            staticMode={staticMode}
            onToggle={onToggleMotion}
            size={520}
          />
        </div>
      </section>

      <section
        className="principles"
        id="principles"
        aria-labelledby="principles-title"
      >
        <div>
          <p className="eyebrow">The foundation</p>
          <h2 id="principles-title">One world. Focused forces.</h2>
        </div>
        <div className="principles__body">
          <p>
            Quiddity is the shared threshold. Each Engine holds a bounded
            capability, a clear contract, and the freedom to evolve without
            becoming interchangeable.
          </p>
          <dl className="principle-list">
            <div>
              <dt>Darkness</dt>
              <dd>Rest, not threat.</dd>
            </div>
            <div>
              <dt>Light</dt>
              <dd>Manifestation, not spectacle.</dd>
            </div>
            <div>
              <dt>Companionship</dt>
              <dd>Participation without servitude.</dd>
            </div>
          </dl>
        </div>
      </section>
    </main>
  );
}

function QuaeroWorkspace() {
  const [{ reference: jectReference, resumeTrajectoryId }] = useState(currentJectHandoff);
  const [text, setText] = useState("");
  const [result, setResult] = useState<QuaeroResponse | null>(null);
  const [questionAnswers, setQuestionAnswers] = useState<
    Record<string, string>
  >({});
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [activeAction, setActiveAction] = useState("");

  if (jectReference || resumeTrajectoryId) {
    return (
      <section className="quaero-workspace" id="quaero-workspace">
        <JectQuaeroHandoff reference={jectReference} resumeTrajectoryId={resumeTrajectoryId} />
      </section>
    );
  }

  const updateFormation = (formation: QuaeroRequestFormation) => {
    setResult((current) =>
      current
        ? {
            ...current,
            requests: current.requests.map((item) =>
              item.requestId === formation.requestId ? formation : item,
            ),
          }
        : current,
    );
  };

  const refreshStream = async (streamId: string) => {
    const response = await fetch(`/api/quaero/v1/streams/${streamId}`, {
      credentials: "same-origin",
    });
    const body = (await response.json()) as
      | QuaeroStreamResponse
      | { detail?: string };
    if (!response.ok || !("requests" in body)) {
      throw new Error(
        "detail" in body && body.detail
          ? body.detail
          : "Quaero could not refresh the request state.",
      );
    }
    setResult((current) =>
      current ? { ...current, requests: body.requests } : current,
    );
  };

  const interpret = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submission = text.trim();
    if (!submission) {
      setError("Enter the fictional, non-sensitive action you want requested.");
      return;
    }

    setWorking(true);
    setError("");
    setResult(null);
    setQuestionAnswers({});
    try {
      const response = await fetch("/api/quaero/v1/intakes", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: submission,
          dataClassification: "fictional-non-sensitive-poc",
          purpose: "action-request",
          actionContext: { origin: "direct" },
        }),
      });
      const body = (await response.json()) as
        | QuaeroResponse
        | { detail?: string };
      if (!response.ok) {
        throw new Error(
          "detail" in body && body.detail
            ? body.detail
            : "Quaero could not form this action request.",
        );
      }
      if (
        !("candidates" in body) ||
        !Array.isArray(body.candidates) ||
        !("requests" in body) ||
        !Array.isArray(body.requests)
      ) {
        throw new Error("Quaero returned an incomplete request formation.");
      }
      setResult(body);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Quaero is temporarily unavailable.",
      );
    } finally {
      setWorking(false);
    }
  };

  const answerQuestion = async (
    event: FormEvent<HTMLFormElement>,
    formation: QuaeroRequestFormation,
  ) => {
    event.preventDefault();
    if (!result || !formation.nextQuestion) return;
    const question = formation.nextQuestion;
    const answer = (questionAnswers[question.questionEventId] ?? "").trim();
    if (!answer) {
      setError("Answer the current question before continuing.");
      return;
    }
    setActiveAction(`question-${question.questionEventId}`);
    setError("");
    try {
      const response = await fetch(
        `/api/quaero/v1/streams/${result.streamId}/requests/${formation.requestId}/questions/${question.questionEventId}/answers`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            answer,
            dataClassification: "fictional-non-sensitive-poc",
            idempotencyKey: crypto.randomUUID(),
          }),
        },
      );
      const body = (await response.json()) as {
        detail?: string;
        formation?: QuaeroRequestFormation;
      };
      if (!response.ok || !body.formation) {
        throw new Error(body.detail || "Quaero could not record this detail.");
      }
      updateFormation(body.formation);
      setQuestionAnswers((current) => ({
        ...current,
        [question.questionEventId]: "",
      }));
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Quaero is temporarily unavailable.",
      );
    } finally {
      setActiveAction("");
    }
  };

  const prepareHandoff = async (formation: QuaeroRequestFormation) => {
    if (!result) return;
    setActiveAction(`handoff-${formation.requestId}`);
    setError("");
    try {
      const response = await fetch(
        `/api/quaero/v1/streams/${result.streamId}/requests/${formation.requestId}/handoffs`,
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            dataClassification: "fictional-non-sensitive-poc",
            idempotencyKey: crypto.randomUUID(),
          }),
        },
      );
      const body = (await response.json()) as {
        detail?: string;
        handoff?: QuaeroHandoff;
      };
      if (!response.ok || !body.handoff) {
        throw new Error(body.detail || "Quaero could not prepare the handoff.");
      }
      await refreshStream(result.streamId);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Quaero is temporarily unavailable.",
      );
    } finally {
      setActiveAction("");
    }
  };

  return (
    <section
      className="quaero-workspace"
      id="quaero-workspace"
      aria-labelledby="quaero-workspace-title"
    >
      <div className="quaero-workspace__intro">
        <p className="eyebrow">Controlled pilot · Fictional data only</p>
        <h2 id="quaero-workspace-title">What do you want done?</h2>
        <p>
          Quaero establishes a request for action, asks only for material
          missing details, and proposes an unassigned capability route toward
          an authorized person or team. It does not assign or execute the work.
        </p>
      </div>

      <form className="intake-form" onSubmit={interpret} aria-busy={working}>
        <div className="intake-form__label-row">
          <label htmlFor="quaero-input">Your requested action</label>
          <button
            className="text-button"
            type="button"
            onClick={() => setText(FICTIONAL_EXAMPLE)}
          >
            <FunctionalSymbol
              semantic="common.add"
              showLabel={false}
              decorative
            />
            Use a fictional example
          </button>
        </div>
        <textarea
          id="quaero-input"
          value={text}
          onChange={(event) => setText(event.target.value)}
          rows={6}
          maxLength={12000}
          placeholder="I want an authorized person or team to…"
        />
        <div className="intake-form__footer">
          <p>
            Local interpretation may take up to 90 seconds. Do not enter
            sensitive information.
          </p>
          <button className="primary-action" type="submit" disabled={working}>
            <FunctionalSymbol
              semantic="common.send"
              showLabel={false}
              decorative
            />
            {working ? "Forming request…" : "Form request with Quaero"}
          </button>
        </div>
      </form>

      <div className="intake-status" aria-live="polite" aria-atomic="true">
        {working && (
          <p>
            Quaero is identifying the action paths and material missing details.
          </p>
        )}
        {error && <p className="intake-error">{error}</p>}
      </div>

      {result && (
        <section
          className="interpretation-results"
          aria-labelledby="interpretation-title"
        >
          <div className="interpretation-results__header">
            <div>
              <p className="eyebrow">Supporting request analysis</p>
              <h3 id="interpretation-title">
                {result.candidates.length}{" "}
                {result.candidates.length === 1 ? "reading" : "readings"}
              </h3>
            </div>
            <a className="text-action" href="https://ject.quiddity.live/">
              <FunctionalSymbol
                semantic="trajectory.pursuit"
                showLabel={false}
                decorative
              />
              Continue deliberating in Ject
            </a>
          </div>
          <ol className="candidate-list">
            {result.candidates.map((candidate) => (
              <li
                className={`candidate candidate--${candidate.construct}`}
                key={candidate.candidateEventId}
              >
                <div className="candidate__meta">
                  <span>
                    <FunctionalSymbol
                      semantic={`construct.${candidate.construct}`}
                      showLabel={false}
                      decorative
                    />
                    Supporting {candidate.construct} reading
                  </span>
                  <span>{candidate.confidence} confidence</span>
                </div>
                <h4>{candidate.statement}</h4>
                <p>{candidate.rationale}</p>
                <details>
                  <summary>
                    <FunctionalSymbol
                      semantic="common.expand"
                      showLabel={false}
                      decorative
                    />
                    Why this reading?
                  </summary>
                  <dl>
                    <div>
                      <dt>Source</dt>
                      <dd>{candidate.sourceText}</dd>
                    </div>
                    <div>
                      <dt>Signals</dt>
                      <dd>
                        {candidate.signals.length
                          ? candidate.signals.join(", ")
                          : "No lexical signal recorded"}
                      </dd>
                    </div>
                    <div>
                      <dt>Method</dt>
                      <dd>
                        {candidate.provenance.fallback
                          ? `Transparent fallback${candidate.provenance.fallbackClass ? ` · ${candidate.provenance.fallbackClass}` : ""}`
                          : candidate.provenance.model ||
                            candidate.provenance.method}
                      </dd>
                    </div>
                  </dl>
                </details>
              </li>
            ))}
          </ol>

          <section
            className="request-formation"
            aria-labelledby="request-formation-title"
          >
            <div className="request-formation__heading">
              <p className="eyebrow">Request formation</p>
              <h3 id="request-formation-title">
                {result.requests.length} possible{" "}
                {result.requests.length === 1 ? "request" : "requests"}
              </h3>
              <p>
                Quaero asks one material question at a time. Supporting readings
                do not require a stance and do not gate request sufficiency.
              </p>
            </div>
            <ol className="request-list">
              {result.requests.map((formation, index) => (
                <li className="request-card" key={formation.requestId}>
                  <div className="request-card__meta">
                    <span>Request {index + 1}</span>
                    <span>{humanize(formation.state)}</span>
                  </div>
                  <h4>{formation.proposedTitle}</h4>
                  <dl className="request-card__classification">
                    <div>
                      <dt>Work type</dt>
                      <dd>{humanize(formation.workType)}</dd>
                    </div>
                    <div>
                      <dt>Subject</dt>
                      <dd>{humanize(formation.subject)}</dd>
                    </div>
                    <div>
                      <dt>Confidence</dt>
                      <dd>{formation.classificationConfidence}</dd>
                    </div>
                  </dl>
                  <p className="request-card__rationale">
                    {formation.classificationRationale}
                  </p>
                  <dl className="request-card__classification">
                    <div>
                      <dt>Proposed route</dt>
                      <dd>{humanize(formation.routing.capability)}</dd>
                    </div>
                    <div>
                      <dt>Assignment</dt>
                      <dd>{humanize(formation.routing.assignmentStatus)}</dd>
                    </div>
                    <div>
                      <dt>Routing authority</dt>
                      <dd>Authorized router required</dd>
                    </div>
                  </dl>
                  <p className="request-card__rationale">
                    {formation.routing.rationale}
                  </p>

                  {formation.nextQuestion && (
                    <form
                      className="question-card"
                      onSubmit={(event) => answerQuestion(event, formation)}
                    >
                      <label
                        htmlFor={`question-${formation.nextQuestion.questionEventId}`}
                      >
                        {formation.nextQuestion.prompt}
                      </label>
                      <p>{formation.nextQuestion.why}</p>
                      {formation.nextQuestion.responseType ===
                      "single-select" ? (
                        <select
                          id={`question-${formation.nextQuestion.questionEventId}`}
                          value={
                            questionAnswers[
                              formation.nextQuestion.questionEventId
                            ] ?? ""
                          }
                          onChange={(event) =>
                            setQuestionAnswers((current) => ({
                              ...current,
                              [formation.nextQuestion!.questionEventId]:
                                event.target.value,
                            }))
                          }
                        >
                          <option value="">Choose one</option>
                          {formation.nextQuestion.options.map((option) => (
                            <option value={option} key={option}>
                              {humanize(option)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <textarea
                          id={`question-${formation.nextQuestion.questionEventId}`}
                          rows={3}
                          value={
                            questionAnswers[
                              formation.nextQuestion.questionEventId
                            ] ?? ""
                          }
                          onChange={(event) =>
                            setQuestionAnswers((current) => ({
                              ...current,
                              [formation.nextQuestion!.questionEventId]:
                                event.target.value,
                            }))
                          }
                        />
                      )}
                      <button
                        className="secondary-action"
                        type="submit"
                        disabled={
                          activeAction ===
                          `question-${formation.nextQuestion.questionEventId}`
                        }
                      >
                        <FunctionalSymbol
                          semantic="common.add"
                          showLabel={false}
                          decorative
                        />
                        {activeAction ===
                        `question-${formation.nextQuestion.questionEventId}`
                          ? "Adding…"
                          : "Add detail"}
                      </button>
                    </form>
                  )}

                  {formation.canPrepareHandoff && (
                    <div className="request-ready">
                      <div>
                        <strong>Sufficient for a temporary handoff</strong>
                        <p>
                          Required action details are present. The capability
                          route remains proposed and unassigned.
                        </p>
                      </div>
                      <button
                        className="primary-action"
                        type="button"
                        onClick={() => prepareHandoff(formation)}
                        disabled={
                          activeAction === `handoff-${formation.requestId}`
                        }
                      >
                        <FunctionalSymbol
                          semantic="common.send"
                          showLabel={false}
                          decorative
                        />
                        {activeAction === `handoff-${formation.requestId}`
                          ? "Preparing…"
                          : "Prepare handoff"}
                      </button>
                    </div>
                  )}

                  {formation.handoff && (
                    <article
                      className="handoff"
                      aria-label="Handoff-ready request"
                    >
                      <p className="eyebrow">Ready for another person</p>
                      <h5>{formation.handoff.proposedTitle}</h5>
                      <dl>
                        <div>
                          <dt>Requested outcome</dt>
                          <dd>{formation.handoff.requestedOutcome}</dd>
                        </div>
                        <div>
                          <dt>Acceptance signals</dt>
                          <dd>{formation.handoff.acceptanceSignals}</dd>
                        </div>
                        <div>
                          <dt>Proposed route</dt>
                          <dd>{humanize(formation.handoff.routing.capability)}</dd>
                        </div>
                        <div>
                          <dt>Assignment</dt>
                          <dd>Unassigned · authorized router required</dd>
                        </div>
                      </dl>
                      {formation.handoff.openUncertainties.length > 0 && (
                        <p>
                          Open but non-blocking:{" "}
                          {formation.handoff.openUncertainties
                            .map(humanize)
                            .join(", ")}
                          .
                        </p>
                      )}
                    </article>
                  )}
                </li>
              ))}
            </ol>
          </section>
        </section>
      )}
    </section>
  );
}

function CommandCenter({
  staticMode,
  onToggleMotion,
  profile,
}: {
  staticMode: boolean;
  onToggleMotion: () => void;
  profile: SessionProfile;
}) {
  const firstName = profileFirstName(profile);

  return (
    <main id="main-content" className="command-center">
      <section className="command-intro" aria-labelledby="command-title">
        <div>
          <p className="eyebrow">Command center · Threshold</p>
          <h1 id="command-title">
            {firstName ? `Welcome back, ${firstName}.` : "Welcome back."}
          </h1>
          <p className="lede">
            Begin with what needs attention. Quiddity will help you find the
            focused Engine that can hold it.
          </p>
        </div>
        <div className="command-presence">
          <QuiddityActivity
            state="breathing"
            size="presence"
            label="Quiddity is ready"
            active={!staticMode}
            announce={false}
          />
          <OrbitalMotionToggle
            staticMode={staticMode}
            onToggle={onToggleMotion}
            size={210}
          />
        </div>
      </section>

      <section className="pilot-boundary" aria-labelledby="pilot-boundary-title">
        <div>
          <p className="eyebrow">Controlled pilot · Know the boundary</p>
          <h2 id="pilot-boundary-title">Fictional, non-sensitive use only.</h2>
        </div>
        <ul>
          <li>
            AI readings are attributed proposals—not facts, diagnosis, consent,
            assignment, or authority.
          </li>
          <li>
            Ject records expire after 30 days of inactivity; backups may retain
            deleted records for up to seven additional days.
          </li>
          <li>
            Use Ject’s export and delete controls. Contact the founder for
            access suspension or credential reset.
          </li>
        </ul>
      </section>

      <section
        className="semantic-review-entry"
        aria-labelledby="semantic-review-entry-title"
      >
        <div>
          <p className="eyebrow">Pilot operations · Founder only</p>
          <h2 id="semantic-review-entry-title">Inspect semantic attention.</h2>
          <p>
            Review one Ject Project’s reference registry, participant
            associations, durable suppressions, and reading suggestions. All
            decisions remain in Ject or Hierophant.
          </p>
        </div>
        <div className="semantic-review-entry__action">
          <p>Read-only Quiddity projection</p>
          <a className="primary-action" href="/app/semantic-review">
            <FunctionalSymbol
              semantic="common.next"
              showLabel={false}
              decorative
            />
            Review semantic state
          </a>
        </div>
      </section>

      <section
        className="available-engines"
        aria-labelledby="available-engines-title"
      >
        <div className="section-heading">
          <p className="eyebrow">Available now</p>
          <h2 id="available-engines-title">Three focused ways in.</h2>
        </div>
        <div className="engine-grid">
          <article className="engine-card engine-card--ject">
            <div className="engine-card__mark">
              <QuiddityMark
                engine="ject"
                decorative
                size={180}
                motion={staticMode ? "static" : "auto"}
              />
            </div>
            <div>
              <p className="engine-card__status">Controlled participant pilot</p>
              <h3>Ject</h3>
              <p className="engine-subtitle">
                Deliberation &amp; Shared Meaning Engine
              </p>
              <p>
                Revisit a commitment, explore an idea, examine a belief, or
                refine an agreement while preserving how your understanding
                changes.
              </p>
              <a className="primary-action" href="https://ject.quiddity.live/">
                <FunctionalSymbol
                  semantic="trajectory.pursuit"
                  showLabel={false}
                  decorative
                />
                Open Ject
              </a>
            </div>
          </article>

          <article className="engine-card engine-card--quaero">
            <div className="quaero-orbit" aria-hidden="true">
              <span>Q</span>
            </div>
            <div>
              <p className="engine-card__status">Controlled participant pilot</p>
              <h3>Quaero</h3>
              <p className="engine-subtitle">
                Action Request Formation &amp; Capability Routing Engine
              </p>
              <p>
                Establish a request for action, gather only material missing
                details, and propose an unassigned capability route.
              </p>
              <a className="primary-action" href="/quaero">
                <FunctionalSymbol
                  semantic="common.next"
                  showLabel={false}
                  decorative
                />
                Start with Quaero
              </a>
            </div>
          </article>

          <article className="engine-card engine-card--hollow-hallway">
            <div className="hollow-hallway-orbit" aria-hidden="true">
              <FunctionalSymbol
                semantic="scene.experience"
                showLabel={false}
                decorative
                size={54}
              />
            </div>
            <div>
              <p className="engine-card__status">Founder-only first-person POC</p>
              <h3>Hollow Hallway</h3>
              <p className="engine-subtitle">
                Interactive Narrative &amp; Synthetic Character Experience
              </p>
              <p>
                Move through one fictional scene, interact with bounded objects,
                meet a visibly synthetic character, and let conversation
                influence the next narrated beat.
              </p>
              <a className="primary-action" href="/app/hollow-hallway">
                <FunctionalSymbol
                  semantic="narrative.continue"
                  showLabel={false}
                  decorative
                />
                Enter Hollow Hallway
              </a>
            </div>
          </article>
        </div>
      </section>

      <section className="engine-horizon" aria-labelledby="horizon-title">
        <div>
          <p className="eyebrow">Constellation</p>
          <h2 id="horizon-title">
            <a href="/engines#roadmap">Planned Engines</a>
          </h2>
        </div>
        <ul aria-label="Planned Engines">
          <li>
            <span>Histor</span>
            <small>Provenance and trust ledger</small>
          </li>
          <li>
            <span>Echo</span>
            <small>Continuity and restoration</small>
          </li>
          <li>
            <span>Aegis</span>
            <small>Protected interface and policy</small>
          </li>
          <li>
            <span>Sceop</span>
            <small>Multimedia generation orchestration</small>
          </li>
        </ul>
      </section>
    </main>
  );
}

export function legacyProtectedTarget(pathname: string, hash: string) {
  if (pathname === "/engine" || pathname === "/engine/") {
    return `/engines${hash}`;
  }
  if (pathname === "/engine/prompts" || pathname === "/engine/prompts/") {
    return "/engines/prompts";
  }
  if (pathname === "/app" && hash === "#quaero-workspace") return "/quaero";
  if (pathname === "/app" && hash === "#public-knowledge") {
    return "https://ject.quiddity.live/public-knowledge/";
  }
  return null;
}

export function protectedSignInUrl(pathname: string, search: string, hash: string) {
  const returnTo = `${pathname}${search}${hash}`;
  return `/oauth2/start?rd=${encodeURIComponent(returnTo)}`;
}

function PrivateSessionGate({ status }: { status: Exclude<ProtectedSession["status"], "authenticated"> }) {
  const message =
    status === "error"
      ? "Your session could not be verified. Reload to try again."
      : status === "unauthenticated"
        ? "Sign-in is required. Taking you to Quiddity sign-in…"
        : "Checking your private session…";
  return (
    <main
      id="main-content"
      className="route-loading route-loading--private"
      aria-busy={status === "checking"}
    >
      <QuiddityActivity
        state={status === "error" ? "breathing" : "connecting"}
        size="presence"
        label={message}
        active={status !== "error"}
      />
    </main>
  );
}

function useProtectedSession(enabled: boolean): ProtectedSession {
  const [session, setSession] = useState<ProtectedSession>(
    enabled
      ? { status: "checking", profile: null }
      : { status: "idle", profile: null },
  );

  useEffect(() => {
    if (!enabled) {
      setSession({ status: "idle", profile: null });
      return;
    }

    let controller: AbortController | null = null;
    let disposed = false;
    const mode = (import.meta as ImportMeta & { env?: { MODE?: string } }).env?.MODE;

    const verify = () => {
      controller?.abort();
      controller = new AbortController();
      setSession({ status: "checking", profile: null });
      fetch("/api/session/v1/profile", {
        credentials: "same-origin",
        cache: "no-store",
        signal: controller.signal,
      })
        .then(async (response) => {
          if (response.status === 401 || response.status === 403) {
            return { status: "unauthenticated", profile: null } as const;
          }
          if (!response.ok) {
            return { status: "error", profile: null } as const;
          }
          return {
            status: "authenticated",
            profile: (await response.json()) as SessionProfile,
          } as const;
        })
        .then((nextSession) => {
          if (disposed) return;
          setSession(nextSession);
          if (nextSession.status === "unauthenticated" && mode !== "test") {
            window.location.replace(
              protectedSignInUrl(
                window.location.pathname,
                window.location.search,
                window.location.hash,
              ),
            );
          }
        })
        .catch((error: unknown) => {
          if (
            !disposed &&
            !(error instanceof DOMException && error.name === "AbortError")
          ) {
            setSession({ status: "error", profile: null });
          }
        });
    };

    const verifyVisibleSession = () => {
      if (document.visibilityState === "visible") verify();
    };

    verify();
    window.addEventListener("pageshow", verify);
    window.addEventListener("focus", verify);
    document.addEventListener("visibilitychange", verifyVisibleSession);
    return () => {
      disposed = true;
      controller?.abort();
      window.removeEventListener("pageshow", verify);
      window.removeEventListener("focus", verify);
      document.removeEventListener("visibilitychange", verifyVisibleSession);
    };
  }, [enabled]);

  return session;
}

export function App() {
  const legacyTarget = legacyProtectedTarget(window.location.pathname, window.location.hash);
  useEffect(() => {
    const mode = (import.meta as ImportMeta & { env?: { MODE?: string } }).env?.MODE;
    if (legacyTarget && mode !== "test") window.location.replace(legacyTarget);
  }, [legacyTarget]);
  const promptsView =
    window.location.pathname === "/engines/prompts" ||
    window.location.pathname.startsWith("/engines/prompts/");
  const portfolioView =
    window.location.pathname === "/engines";
  const hollowHallwayView =
    window.location.pathname === "/app/hollow-hallway" ||
    window.location.pathname.startsWith("/app/hollow-hallway/");
  const semanticReviewView =
    window.location.pathname === "/app/semantic-review" ||
    window.location.pathname.startsWith("/app/semantic-review/");
  const quaeroView =
    window.location.pathname === "/quaero" ||
    window.location.pathname.startsWith("/quaero/");
  const commandCenterView =
    !legacyTarget &&
    (window.location.pathname === "/app" ||
      (window.location.pathname.startsWith("/app/") && !hollowHallwayView));
  const { staticMode, toggle } = useMotionPreference();
  const protectedSession = useProtectedSession(commandCenterView);
  const protectedView =
    promptsView ||
    portfolioView ||
    quaeroView ||
    window.location.pathname === "/app" ||
    window.location.pathname.startsWith("/app/");

  if (legacyTarget) {
    return <main id="main-content" className="route-loading" aria-busy="true"><p>Taking you to the new protected space…</p></main>;
  }

  if (commandCenterView && protectedSession.status !== "authenticated") {
    return <PrivateSessionGate status={protectedSession.status} />;
  }

  return (
    <div className={staticMode ? "app app--static" : "app"}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <Header
        protectedView={protectedView}
        profile={commandCenterView ? protectedSession.profile : null}
      />
      <Suspense fallback={<main id="main-content" className="route-loading" aria-busy="true"><p>Opening this Quiddity space…</p></main>}>
        {promptsView ? (
          <PromptLibrary />
        ) : portfolioView ? (
          <EnginePortfolio />
        ) : quaeroView ? (
          <QuaeroWorkspace />
        ) : hollowHallwayView ? (
          <HollowHallway staticMode={staticMode} />
        ) : semanticReviewView ? (
          <SemanticReview />
        ) : protectedView ? (
          <CommandCenter
            staticMode={staticMode}
            onToggleMotion={toggle}
            profile={protectedSession.profile as SessionProfile}
          />
        ) : (
          <Landing staticMode={staticMode} onToggleMotion={toggle} />
        )}
      </Suspense>
      <footer className="site-footer">
        <span>Quiddity is lived.</span>
        <span>© 2026 Quiddity</span>
      </footer>
    </div>
  );
}
